/**
 * Attention item utilities for the Client Workspace.
 *
 * Derives actionable attention items from project state at query time.
 * No separate attention table — items are computed from project fields.
 *
 * - `computeAttentionItems` — derives attention items from project state
 * - `sortAttentionItems` — sorts by priority hierarchy, then recency within type
 *
 * Priority hierarchy (highest → lowest):
 *   overdue → approval_required → revision_request → unread_message → new_upload
 *
 * @module attention
 * @see Requirements 6.1, 6.2
 */

import type {
  ClientProjectSummaryDto,
  AttentionItemDto,
  AttentionItemType,
} from '../../../../types/client-workspace';

/**
 * Priority ranking for attention item types.
 * Lower number = higher priority.
 */
const ATTENTION_PRIORITY: Record<AttentionItemType, number> = {
  overdue: 0,
  approval_required: 1,
  revision_request: 2,
  unread_message: 3,
  new_upload: 4,
};

/**
 * Computes attention items from project state.
 *
 * Derives items based on:
 * - `overdue`: project has `dueDate` in the past
 * - `approval_required`: project status is `awaiting_review`
 * - `revision_request`: project status is `revision_requested`
 * - `unread_message`: project `hasUnreadMessages === true`
 * - `new_upload`: project `hasNewActivity === true` (simplified heuristic)
 *
 * Each item gets a synthetic ID in the format `${projectId}:${type}`.
 *
 * @param projects - Array of project summaries to derive attention items from
 * @returns Sorted array of attention items (sorted by priority, then recency)
 */
export function computeAttentionItems(
  projects: ClientProjectSummaryDto[],
): AttentionItemDto[] {
  const items: AttentionItemDto[] = [];

  for (const project of projects) {
    // Overdue: project has dueDate in the past
    if (project.dueDate && new Date(project.dueDate) < new Date()) {
      items.push({
        id: `${project.id}:overdue`,
        type: 'overdue',
        sourceName: project.name,
        sourceProjectId: project.id,
        actionableAt: project.dueDate,
      });
    }

    // Approval required: status is awaiting_review
    if (project.status === 'awaiting_review') {
      items.push({
        id: `${project.id}:approval_required`,
        type: 'approval_required',
        sourceName: project.name,
        sourceProjectId: project.id,
        actionableAt: project.lastActivityAt,
      });
    }

    // Revision request: status is revision_requested
    if (project.status === 'revision_requested') {
      items.push({
        id: `${project.id}:revision_request`,
        type: 'revision_request',
        sourceName: project.name,
        sourceProjectId: project.id,
        actionableAt: project.lastActivityAt,
      });
    }

    // Unread message: hasUnreadMessages flag
    if (project.hasUnreadMessages) {
      items.push({
        id: `${project.id}:unread_message`,
        type: 'unread_message',
        sourceName: project.name,
        sourceProjectId: project.id,
        actionableAt: project.lastActivityAt,
      });
    }

    // New upload: hasNewActivity flag (simplified heuristic)
    if (project.hasNewActivity) {
      items.push({
        id: `${project.id}:new_upload`,
        type: 'new_upload',
        sourceName: project.name,
        sourceProjectId: project.id,
        actionableAt: project.lastActivityAt,
      });
    }
  }

  return sortAttentionItems(items);
}

/**
 * Sorts attention items by priority hierarchy, then by recency within each type.
 *
 * Priority order (highest first):
 *   overdue → approval_required → revision_request → unread_message → new_upload
 *
 * Within the same type, items are sorted by `actionableAt` descending (newest first).
 *
 * @param items - Array of attention items to sort
 * @returns A new sorted array (does not mutate the input)
 */
export function sortAttentionItems(items: AttentionItemDto[]): AttentionItemDto[] {
  return [...items].sort((a, b) => {
    // Primary sort: priority hierarchy (lower number = higher priority)
    const priorityDiff = ATTENTION_PRIORITY[a.type] - ATTENTION_PRIORITY[b.type];
    if (priorityDiff !== 0) return priorityDiff;

    // Secondary sort: recency within same type (newest first)
    return new Date(b.actionableAt).getTime() - new Date(a.actionableAt).getTime();
  });
}
